<template>
  <div class="min-h-screen flex items-center justify-center px-4">
    <div class="max-w-md w-full space-y-8">
      <!-- Logo/Header -->
      <div class="text-center">
        <h1 class="text-4xl font-bold text-primary-600">🛒</h1>
        <h2 class="mt-4 text-3xl font-bold">{{ $t('auth.passwordReset.title') }}</h2>
        <p class="mt-2 text-gray-600">
          {{ $t('auth.passwordReset.description') }}
        </p>
      </div>

      <!-- Success Message -->
      <div v-if="successMessage" class="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
        {{ successMessage }}
      </div>

      <!-- Error Message -->
      <div v-if="errorMessage" class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
        {{ errorMessage }}
      </div>

      <!-- Loading State -->
      <div v-if="verifying" class="text-center text-gray-600">
        <p>{{ $t('auth.passwordReset.verifying') }}</p>
      </div>

      <!-- Form (show only if token is valid and not yet successful) -->
      <form v-else-if="tokenValid && !successMessage" @submit.prevent="handleSubmit" class="mt-8 space-y-6">
        <div class="space-y-4">
          <div>
            <label for="newPassword" class="block text-sm font-medium text-gray-700 mb-1">
              {{ $t('auth.passwordReset.newPassword') }}
            </label>
            <input
              id="newPassword"
              v-model="newPassword"
              type="password"
              required
              class="input"
              placeholder="••••••••"
              :disabled="loading"
            />
          </div>

          <div>
            <label for="newPasswordConfirm" class="block text-sm font-medium text-gray-700 mb-1">
              {{ $t('auth.passwordReset.confirmNewPassword') }}
            </label>
            <input
              id="newPasswordConfirm"
              v-model="newPasswordConfirm"
              type="password"
              required
              class="input"
              placeholder="••••••••"
              :disabled="loading"
            />
          </div>
        </div>

        <button
          type="submit"
          class="w-full btn btn-primary"
          :disabled="loading"
        >
          {{ loading ? $t('auth.passwordReset.resetting') : $t('auth.passwordReset.button') }}
        </button>
      </form>

      <!-- Back to Login -->
      <div class="text-center">
        <router-link
          to="/login"
          class="text-primary-600 hover:text-primary-500 font-medium"
        >
          {{ successMessage ? $t('auth.passwordReset.goToSignIn') : $t('auth.passwordReset.backToSignIn') }}
        </router-link>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuth } from '../composables/useAuth'

const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const { verifyPasswordResetToken, confirmPasswordReset } = useAuth()

const token = ref('')
const newPassword = ref('')
const newPasswordConfirm = ref('')
const loading = ref(false)
const verifying = ref(true)
const tokenValid = ref(false)
const errorMessage = ref('')
const successMessage = ref('')

onMounted(async () => {
  // Get token from URL query parameter
  token.value = route.query.token

  if (!token.value) {
    errorMessage.value = t('auth.passwordReset.invalidLink')
    verifying.value = false
    return
  }

  // Verify the token
  const result = await verifyPasswordResetToken(token.value)
  verifying.value = false

  if (result.success) {
    tokenValid.value = true
  } else {
    errorMessage.value = result.error
  }
})

const handleSubmit = async () => {
  errorMessage.value = ''
  successMessage.value = ''
  loading.value = true

  try {
    const result = await confirmPasswordReset(
      token.value,
      newPassword.value,
      newPasswordConfirm.value
    )

    if (result.success) {
      successMessage.value = result.message || t('auth.passwordReset.successMessage')

      // Redirect to login after 3 seconds
      setTimeout(() => {
        router.push('/login')
      }, 3000)
    } else {
      errorMessage.value = result.error
    }
  } finally {
    loading.value = false
  }
}
</script>
